<template>
  <v-card>
    <div v-if="vesselRolesLoading || usersLoading" class="dialog-spinner py-5">
      <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
    </div>
    <template v-else-if="allowedUsers && allowedUsers.length > 0">
      <v-toolbar flat dark color="transparent" style="z-index: 1" absolute>
        <v-btn v-if="$vuetify.breakpoint.xsOnly" icon @click.native="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="flex-grow-1"></div>
        <v-btn v-if="$vuetify.breakpoint.xsOnly" icon :disabled="!valid || !itemComplete" @click="onSubmit(item)">
          <v-icon>mdi-check</v-icon>
        </v-btn>
      </v-toolbar>

      <div v-if="item && user && allowedUsers" class="blue text-center pa-4 white--text" style="position: relative">
        <v-avatar size="158" color="grey lighten-4" class="mb-3">
          <img
            style="border: 4px solid white"
            :src="user.image ? user.image.contentUrl + '?x-request=html' : require('@/assets/no-img.svg')"
            :alt="user.firstName"
          />
        </v-avatar>

        <v-autocomplete
          v-model="selectedUser"
          :items="allowedUsers"
          item-value="@id"
          :item-text="displayName"
          label="User"
          single-line
          required
          :rules="userRules"
          :error-messages="isInvalid('user') ? violations.user : []"
          hide-no-data
          auto-select-first
          dark
          solo-inverted
          flat
          hide-details
          return-object
          @change="handleUpdateField('user', $event)"
        ></v-autocomplete>
      </div>

      <v-card-text v-if="user" class="pa-0 fill-height">
        <v-form ref="form" v-model="valid" class="fill-height" lazy-validation @submit.prevent="onSubmit(item)">
          <div v-if="userLoading" class="dialog-spinner py-5">
            <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
          </div>

          <template v-else>
            <v-list two-line>
              <v-list-item>
                <v-list-item-action>
                  <v-icon color="grey">mdi-account</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>{{ user.username }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('message.user_name') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <v-list-item-action>
                  <v-icon color="grey">mdi-email</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>{{ user.email }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('message.email') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-divider></v-divider>

            <div class="pa-3">
              <v-list-item-title>{{
                $t('message.role_in_vessel_name', { vessel_name: vessel.name })
              }}</v-list-item-title>

              <v-select
                v-model="item.vesselRole"
                :items="vesselRoles"
                item-value="@id"
                :item-text="translateRole"
                :label="$t('message.vessel_role')"
                single-line
                :rules="vesselRoleRules"
                :error-messages="isInvalid('vesselRole') ? violations.vesselRole : []"
                @change.native="handleUpdateField('vesselRole', $event.target.value)"
              ></v-select>
            </div>
          </template>
        </v-form>
      </v-card-text>

      <v-divider class="hidden-xs-only"></v-divider>
      <v-card-actions class="hidden-xs-only">
        <div class="flex-grow-1"></div>
        <v-btn color="grey" text @click.native="$emit('close')">{{ $t('message.close') }}</v-btn>
        <v-btn :disabled="!valid || !itemComplete" color="orange" text @click="onSubmit(item)">
          {{ $t('message.save') }}
        </v-btn>
      </v-card-actions>
    </template>
    <template v-else>
      <v-toolbar flat dark color="blue">
        <v-btn v-if="$vuetify.breakpoint.xsOnly" icon @click.native="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ $t('message.no_employees') }}
        </v-toolbar-title>
      </v-toolbar>

      <v-row align="center" justify="center">
        <div class="text-center pa-5">
          <v-icon size="100" color="grey">mdi-account-off-outline</v-icon>
          <div class="subheading">
            {{ $t('message.no_employees_paragraph') }}
          </div>
          <v-btn text color="blue" @click="$emit('close')">{{ $t('message.close') }}</v-btn>
        </div>
      </v-row>

      <v-divider class="hidden-xs-only"></v-divider>
      <v-card-actions class="hidden-xs-only">
        <div class="flex-grow-1"></div>
        <v-btn color="grey" text @click.native="$emit('close')">{{ $t('message.close') }}</v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import fetch from '@/utils/fetch'

export default {
  props: {
    handleSubmit: {
      type: Function,
      required: true
    },

    handleUpdateField: {
      type: Function,
      required: true
    },

    values: {
      type: Object,
      required: true
    },

    errors: {
      type: Object,
      default: () => {}
    },

    initialValues: {
      type: Object,
      default: () => {}
    },

    vessel: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      valid: true,
      vesselRoles: null,
      users: null,
      vesselRolesLoading: false,
      user: null,
      usersLoading: false,
      userLoading: false,
      selectedUser: null,
      userRules: [
        v => {
          return !!v || 'User is required'
        }
      ],
      vesselRoleRules: [
        v => {
          return !!v || 'Vessel role is required'
        }
      ]
    }
  },

  computed: {
    // eslint-disable-next-line
    item () {
      return this.initialValues || this.values
    },

    violations() {
      return this.errors || {}
    },

    allowedUsers() {
      if (!this.users) {
        return
      }
      const allowedUsers = []

      this.users.forEach(user => {
        let isOrgMember = false
        if (user.roleInOrganizations && user.roleInOrganizations.length > 0) {
          user.roleInOrganizations.forEach(roleInOrganization => {
            if (
              roleInOrganization.organization['@id'] === this.vessel.organization['@id'] &&
              'ROLE_MEMBER' === roleInOrganization.organizationRole.name
            ) {
              isOrgMember = true
            }
          })
        }

        if (isOrgMember) {
          let hasRoleInCurrentVessel = false
          if (user.roleInVessels && user.roleInVessels.length > 0) {
            user.roleInVessels.forEach(roleInVessel => {
              if (roleInVessel.vessel === this.vessel['@id']) {
                hasRoleInCurrentVessel = true
              }
            })
          }

          if (!hasRoleInCurrentVessel) {
            allowedUsers.push(user)
          }
        }
      })

      return allowedUsers
    },

    itemComplete() {
      if (!this.user) {
        return
      }
      return this.item.user && this.item.vesselRole
    }
  },

  watch: {
    allowedUsers: function(allowedUsers) {
      if (!allowedUsers) {
        return
      }
      if (allowedUsers && allowedUsers.length > 0) {
        this.selectedUser = allowedUsers[0]
        this.item.user = allowedUsers[0]
        this.fetchUser(allowedUsers[0]['@id'])
      }
    },

    selectedUser: function(selectedUser) {
      this.fetchUser(selectedUser['@id'])
    },

    user: function(user) {
      if (!user) {
        return
      }

      this.item.user = user
    }
  },

  created() {
    this.fetchVesselRoles()
    this.fetchUsers()
  },

  methods: {
    displayName(user) {
      return user.firstName + ' ' + user.lastName
    },

    translateRole(role) {
      return this.$t('message.' + role.name)
    },

    fetchVesselRoles() {
      this.vesselRolesLoading = true
      fetch('/vessel_roles?pagination=false')
        .then(response => response.json())
        .then(data => {
          this.vesselRoles = data['hydra:member']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
        .then(() => {
          this.vesselRolesLoading = false
        })
    },

    fetchUsers() {
      this.usersLoading = true
      fetch('/users?pagination=false')
        .then(response => response.json())
        .then(data => {
          this.users = data['hydra:member']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
        .then(() => {
          this.usersLoading = false
        })
    },

    fetchUser(id) {
      this.userLoading = true
      fetch(id)
        .then(response => response.json())
        .then(data => {
          this.user = data
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
        .then(() => {
          this.userLoading = false
        })
    },

    isInvalid(key) {
      return Object.keys(this.violations).length > 0 && this.violations[key]
    },

    onSubmit(item) {
      if (this.$refs.form.validate()) {
        let cleanItem = { ...item }

        if (
          cleanItem.user.roleInOrganizations[0].organization &&
          'string' !== typeof cleanItem.user.roleInOrganizations[0].organization
        ) {
          cleanItem.user.roleInOrganizations[0].organization = cleanItem.user.roleInOrganizations[0].organization['@id']
        }

        if (
          cleanItem.user.roleInOrganizations[0].organizationRole &&
          'string' !== typeof cleanItem.user.roleInOrganizations[0].organizationRole
        ) {
          cleanItem.user.roleInOrganizations[0].organizationRole =
            cleanItem.user.roleInOrganizations[0].organizationRole['@id']
        }
        this.handleSubmit(cleanItem)
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.hacked-divider {
  max-width: calc(100% + 32px);
  width: calc(100% + 32px);
  margin-left: -16px;
}
</style>
